import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Search,
  X,
  LayoutDashboard,
  Server,
  Activity,
  Brain,
  Ticket,
  AlertTriangle,
  Users,
  Settings,
  Zap,
  FileText,
  Database,
  Globe,
  TrendingUp,
  ChevronRight,
  Command,
  Plus,
  Scale,
  Sparkles,
  Clock,
  BarChart3,
  MessageSquare,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useNavigate } from 'react-router-dom';
import { matchesDashboardSearch } from '../../lib/dashboardSearchMatch';
import { isTeamChatEnabled } from '../../modules/team-chat/featureFlag';
import { teamService } from '../../services/team/TeamService';
import { dispatchOpenTeamChatFloating } from '../../modules/team-chat/utils/teamChatEvents';
import { INTEGRATIONS_SEARCH_ENTRIES } from '../../modules/integrations/integrationsSearchEntries';

interface CommandItem {
  id: string;
  title: string;
  description: string;
  category: string;
  path?: string;
  action?: () => void;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
  keywords?: string[];
  type: 'navigation' | 'command' | 'ai-query';
  /** Shift+Enter opens floating DM when team chat is enabled */
  floatPeerId?: string;
}

interface RecentCommand {
  id: string;
  title: string;
  timestamp: Date;
}

interface CommandCenterProps {
  isOpen: boolean;
  onClose: () => void;
}

// AI-powered query responses
const handleAIQuery = (queryId: string): string => {
  const responses: Record<string, string> = {
    'ai-why-slow': '🤖 Analysis complete: Your API shows 280% latency increase. Primary cause: Redis memory saturation at 95%. Recommendation: Scale Redis or add read replica.',
    'ai-predict-outage': '🤖 Prediction: 82% probability of auth-service outage in next 4 hours due to increasing error rate and memory leak pattern.',
    'ai-top-failing': '🤖 Top failing services: 1) payment-api (15% error rate), 2) notification-service (8% errors), 3) auth-service (5% errors).',
    'ai-cost-optimization': '🤖 Found 3 opportunities: 1) Downscale prod-east cluster (23% avg CPU) - Save $2,100/mo, 2) Archive old logs - Save $450/mo, 3) Right-size RDS instances - Save $890/mo.',
    'ai-anomaly-detection': '🤖 Detected 2 anomalies: 1) Unusual traffic spike on /api/checkout (+340%), 2) Memory leak in user-service (growing 2MB/hour).'
  };
  return responses[queryId] || '🤖 AI analysis in progress...';
};

// All command items combined
const allCommands: CommandItem[] = [
  // AI Queries
  { 
    id: 'ai-why-slow', 
    title: 'Why is my API slow?', 
    description: 'AI analysis of API performance issues', 
    category: 'AI Queries', 
    type: 'ai-query',
    icon: Brain, 
    badge: 'AI',
    keywords: ['slow', 'api', 'performance', 'latency', 'why']
  },
  { 
    id: 'ai-predict-outage', 
    title: 'Predict next outage', 
    description: 'AI-powered outage prediction', 
    category: 'AI Queries', 
    type: 'ai-query',
    icon: Brain, 
    badge: 'AI',
    keywords: ['predict', 'outage', 'forecast', 'incident']
  },
  { 
    id: 'ai-top-failing', 
    title: 'Show top failing services', 
    description: 'AI analysis of service health', 
    category: 'AI Queries', 
    type: 'ai-query',
    icon: Brain, 
    badge: 'AI',
    keywords: ['failing', 'services', 'errors', 'issues', 'top']
  },
  { 
    id: 'ai-cost-optimization', 
    title: 'Find cost optimization opportunities', 
    description: 'AI-powered cost analysis', 
    category: 'AI Queries', 
    type: 'ai-query',
    icon: Brain, 
    badge: 'AI',
    keywords: ['cost', 'optimization', 'savings', 'reduce', 'money']
  },
  { 
    id: 'ai-anomaly-detection', 
    title: 'Detect anomalies in my infrastructure', 
    description: 'AI-powered anomaly detection', 
    category: 'AI Queries', 
    type: 'ai-query',
    icon: Brain, 
    badge: 'AI',
    keywords: ['anomaly', 'detection', 'unusual', 'abnormal', 'detect']
  },

  // Action Commands
  { 
    id: 'create-project', 
    title: 'Create Project', 
    description: 'Add a new monitoring project', 
    category: 'Actions', 
    path: '/onboarding/add-project',
    type: 'command',
    icon: Plus, 
    keywords: ['create', 'project', 'new', 'add']
  },
  { 
    id: 'add-server', 
    title: 'Add Server', 
    description: 'Add a new server to monitor', 
    category: 'Actions', 
    path: '/onboarding/add-infrastructure',
    type: 'command',
    icon: Server, 
    keywords: ['add', 'server', 'infrastructure', 'new']
  },
  { 
    id: 'create-ticket', 
    title: 'Create Ticket', 
    description: 'Open a new support ticket', 
    category: 'Actions', 
    path: '/ticketing/create',
    type: 'command',
    icon: Ticket, 
    keywords: ['create', 'ticket', 'issue', 'new']
  },
  { 
    id: 'scale-cluster', 
    title: 'Scale Kubernetes Cluster', 
    description: 'Scale your K8s cluster', 
    category: 'Actions',
    type: 'command',
    icon: Scale, 
    keywords: ['scale', 'kubernetes', 'cluster', 'k8s', 'resize'],
    action: () => console.log('Opening Kubernetes scaling interface...')
  },
  { 
    id: 'open-war-room', 
    title: 'Open War Room', 
    description: 'Create incident war room', 
    category: 'Actions', 
    path: '/incident-commander/war-room/wr-001',
    type: 'command',
    icon: AlertTriangle, 
    keywords: ['war room', 'incident', 'create', 'emergency']
  },
  { 
    id: 'run-health-check', 
    title: 'Run System Health Check', 
    description: 'Execute full system diagnostic', 
    category: 'Actions',
    type: 'command',
    icon: Activity, 
    keywords: ['health', 'check', 'diagnostic', 'system', 'run'],
    action: () => console.log('Running system health check...')
  },
  { 
    id: 'assign-incident', 
    title: 'Assign Incident', 
    description: 'Assign incident to engineer', 
    category: 'Actions',
    path: '/incident-commander/engineers',
    type: 'command',
    icon: Users, 
    keywords: ['assign', 'incident', 'engineer', 'allocate']
  },
  { 
    id: 'view-metrics', 
    title: 'View Service Metrics', 
    description: 'Open metrics dashboard', 
    category: 'Actions',
    path: '/monitoring/app-performance',
    type: 'command',
    icon: BarChart3, 
    keywords: ['metrics', 'view', 'dashboard', 'stats', 'show']
  },

  // Navigation
  { id: 'super-dashboard', title: 'Super Dashboard', description: 'Main monitoring dashboard', category: 'Navigation', path: '/overview/super-dashboard', icon: LayoutDashboard, type: 'navigation', keywords: ['dashboard', 'main', 'overview', 'monitoring'] },
  { id: 'agents', title: 'Agents', description: 'Install and manage monitoring agents', category: 'Navigation', path: '/system/agents', icon: Server, type: 'navigation', keywords: ['agents', 'agent', 'installer', 'token', 'heartbeat', 'daemon', 'collector', 'download', 'observability', 'monitoring agent', 'edge', 'host'] },
  { id: 'system-settings', title: 'System Settings', description: 'Profile, notifications, and preferences', category: 'Navigation', path: '/system/settings', icon: Settings, type: 'navigation', keywords: ['settings', 'preferences', 'configuration', 'account', 'profile'] },
  { id: 'incident-commander', title: 'AI Incident Commander', description: 'Autonomous incident management', category: 'Navigation', path: '/incident-commander/dashboard', icon: AlertTriangle, badge: 'AI', type: 'navigation', keywords: ['incident', 'commander', 'ai', 'autonomous'] },
  { id: 'war-rooms', title: 'War Rooms', description: 'Collaborative incident response', category: 'Navigation', path: '/incident-commander/war-room/wr-001', icon: Users, type: 'navigation', keywords: ['war room', 'incident', 'collaboration'] },
  { id: 'ai-insights', title: 'AI Insights', description: 'AI-generated insights', category: 'Navigation', path: '/ai/insights', icon: Sparkles, type: 'navigation', keywords: ['ai', 'insights', 'analytics'] },
  { id: 'servers', title: 'Server Dashboard', description: 'Monitor server health', category: 'Navigation', path: '/server-mgmt/servers', icon: Server, type: 'navigation', keywords: ['servers', 'infrastructure', 'health'] },
  { id: 'app-performance', title: 'Application Performance', description: 'Monitor application metrics', category: 'Navigation', path: '/monitoring/app-performance', icon: TrendingUp, type: 'navigation', keywords: ['apm', 'application', 'performance'] },
  { id: 'errors', title: 'Error Tracking', description: 'Track and analyze errors', category: 'Navigation', path: '/monitoring/errors', icon: AlertTriangle, type: 'navigation', keywords: ['errors', 'bugs', 'issues'] },
  { id: 'microservices', title: 'Microservices Overview', description: 'Monitor all microservices', category: 'Navigation', path: '/microservices/overview', icon: Globe, type: 'navigation', keywords: ['microservices', 'services', 'overview'] },
  { id: 'microservices-fleet', title: 'Fleet Overview', description: 'Microservice observability dashboard', category: 'Navigation', path: '/microservices', icon: Globe, type: 'navigation', keywords: ['fleet', 'microservices', 'observability'] },
  { id: 'microservices-services', title: 'Services Catalog', description: 'Browse microservices', category: 'Navigation', path: '/microservices/services', icon: Globe, type: 'navigation', keywords: ['services', 'catalog', 'microservices'] },
  { id: 'knowledge-graph', title: 'Knowledge Graph', description: 'Incident intelligence memory', category: 'Navigation', path: '/incident-commander/knowledge-graph', icon: Database, type: 'navigation', keywords: ['knowledge', 'graph', 'dependencies'] },
  ...INTEGRATIONS_SEARCH_ENTRIES.map((entry) => ({
    id: entry.id,
    title: entry.title,
    description: entry.description,
    category: 'Integrations' as const,
    path: entry.path,
    icon: entry.icon,
    badge: entry.badge,
    type: 'navigation' as const,
    keywords: entry.keywords,
  })),
];

const categoryIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  'AI Queries': Sparkles,
  'Actions': Zap,
  'Navigation': LayoutDashboard,
  'Recent': Clock,
  'Team Chat': MessageSquare,
  'Integrations': Zap,
};

function buildTeamChatCommandItems(): CommandItem[] {
  if (!isTeamChatEnabled()) return [];
  return teamService.getMembers().map((m) => ({
    id: `team-chat-${m.id}`,
    title: `Chat — ${m.firstName} ${m.lastName}`,
    description: `Team DM · ${m.email}`,
    category: 'Team Chat',
    type: 'command' as const,
    icon: MessageSquare,
    path: `/team/chat?with=${encodeURIComponent(m.id)}`,
    keywords: ['chat', 'slack', 'dm', 'message', 'team', m.email, m.firstName, m.lastName],
    floatPeerId: m.id,
  }));
}

export function CommandCenter({ isOpen, onClose }: CommandCenterProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<CommandItem[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [aiResponse, setAIResponse] = useState<string | null>(null);
  const [recentCommands, setRecentCommands] = useState<RecentCommand[]>([]);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  // Load recent commands from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('coremonit-recent-commands');
    if (stored) {
      setRecentCommands(JSON.parse(stored));
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      setSearchQuery('');
      setResults([]);
      setSelectedIndex(0);
      setAIResponse(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      // Show AI queries and actions when empty; pin Agents in navigation picks for discoverability
      const navigations = allCommands.filter((c) => c.type === 'navigation');
      const navPick = [
        ...navigations.filter((c) => c.id === 'agents'),
        ...navigations.filter((c) => c.id !== 'agents'),
      ].slice(0, 4);
      const teamPick = buildTeamChatCommandItems().slice(0, 4);
      const suggested = [
        ...teamPick,
        ...allCommands.filter((c) => c.type === 'ai-query').slice(0, 3),
        ...allCommands.filter((c) => c.type === 'command').slice(0, 5),
        ...navPick,
      ];
      setResults(suggested);
      setAIResponse(null);
      return;
    }

    const query = searchQuery.toLowerCase();

    const teamItems = buildTeamChatCommandItems();

    const filtered = [...allCommands, ...teamItems].filter((item) =>
      matchesDashboardSearch(searchQuery, [
        item.id,
        item.title,
        item.description,
        item.category,
        ...(item.keywords || []),
      ]),
    );

    // Sort by relevance (exact matches first)
    const sorted = filtered.sort((a, b) => {
      const aTitle = a.title.toLowerCase();
      const bTitle = b.title.toLowerCase();
      if (aTitle.startsWith(query)) return -1;
      if (bTitle.startsWith(query)) return 1;
      return 0;
    });

    setResults(sorted);
    setSelectedIndex(0);
    setAIResponse(null);
  }, [searchQuery]);

  const saveRecentCommand = (command: CommandItem) => {
    const recent: RecentCommand = {
      id: command.id,
      title: command.title,
      timestamp: new Date()
    };
    const updated = [recent, ...recentCommands.filter(c => c.id !== command.id)].slice(0, 10);
    setRecentCommands(updated);
    localStorage.setItem('coremonit-recent-commands', JSON.stringify(updated));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      if (e.shiftKey && results[selectedIndex].floatPeerId) {
        e.preventDefault();
        dispatchOpenTeamChatFloating(results[selectedIndex].floatPeerId!);
        saveRecentCommand(results[selectedIndex]);
        onClose();
      } else {
        handleSelect(results[selectedIndex]);
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleSelect = (item: CommandItem) => {
    saveRecentCommand(item);

    if (item.type === 'ai-query') {
      // Show AI response
      const response = handleAIQuery(item.id);
      setAIResponse(response);
      return;
    }

    if (item.action) {
      item.action();
      onClose();
      return;
    }

    if (item.path) {
      navigate(item.path);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100]"
          />

          {/* Command Center Modal */}
          <div className="fixed inset-0 z-[101] flex items-start justify-center pt-4 sm:pt-20 px-2 sm:px-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -20 }}
              transition={{ duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="w-full max-w-3xl max-h-[calc(100dvh-1rem)] sm:max-h-[calc(100dvh-3rem)] bg-gradient-to-br from-card-bg to-card-bg/80 border border-card-border/50 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            >
              {/* Search Input */}
              <div className="flex items-center gap-3 p-5 border-b border-card-border/30 bg-gradient-to-r from-cyber-cyan/5 to-cyber-blue/5">
                <Sparkles className="w-6 h-6 text-cyber-cyan flex-shrink-0 animate-pulse" />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Search or run command... (try 'why is my API slow')"
                  className="flex-1 bg-transparent text-white placeholder:text-slate-500 outline-none text-lg font-medium"
                />
                <div className="flex items-center gap-2">
                  <Badge className="bg-card-bg/50 text-slate-400 border-slate-600 text-xs px-2 py-1">
                    <Command className="w-3 h-3 mr-1" />
                    K
                  </Badge>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={onClose}
                    className="text-slate-400 hover:text-white hover:bg-card-bg/50 rounded-lg transition-all"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* AI Response */}
              {aiResponse && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="p-4 bg-gradient-to-r from-cyber-cyan/10 to-cyber-blue/10 border-b border-cyber-cyan/30"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyber-cyan to-cyber-blue flex items-center justify-center flex-shrink-0">
                      <Brain className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-semibold text-cyber-cyan">AI Assistant</span>
                        <Badge className="bg-cyber-green/20 text-cyber-green border-cyber-green/30 text-xs">
                          82% Confidence
                        </Badge>
                      </div>
                      <p className="text-sm text-white leading-relaxed">{aiResponse}</p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Results */}
              <div className="flex-1 min-h-0 overflow-y-auto">
                {results.length > 0 ? (
                  <div className="p-2">
                    {/* Group by category */}
                    {Object.entries(
                      results.reduce((acc, result) => {
                        if (!acc[result.category]) acc[result.category] = [];
                        acc[result.category].push(result);
                        return acc;
                      }, {} as Record<string, CommandItem[]>)
                    ).map(([category, items]) => (
                      <div key={category} className="mb-4 last:mb-0">
                        <div className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                          {React.createElement(categoryIcons[category] || LayoutDashboard, {
                            className: 'w-4 h-4'
                          })}
                          {category}
                        </div>
                        <div className="space-y-1">
                          {items.map((result) => {
                            const globalIndex = results.indexOf(result);
                            const isSelected = globalIndex === selectedIndex;
                            return (
                              <motion.div
                                key={result.id}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.2, delay: globalIndex * 0.03 }}
                                onClick={() => handleSelect(result)}
                                className={`flex items-center gap-3 px-3 py-3 rounded-xl cursor-pointer transition-all duration-200 ${ 
                                  isSelected
                                    ? 'bg-gradient-to-r from-cyber-cyan/20 to-cyber-blue/20 border border-cyber-cyan/30 shadow-lg shadow-cyber-cyan/10'
                                    : 'hover:bg-card-bg/50 border border-transparent'
                                }`}
                              >
                                <motion.div 
                                  className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${ 
                                    isSelected
                                      ? 'bg-gradient-to-br from-cyber-cyan to-cyber-blue shadow-lg shadow-cyber-cyan/30'
                                      : 'bg-card-bg/50'
                                  }`}
                                  animate={isSelected ? { scale: [1, 1.05, 1] } : {}}
                                  transition={{ duration: 0.3 }}
                                >
                                  {React.createElement(result.icon, {
                                    className: `w-5 h-5 ${isSelected ? 'text-white' : 'text-cyber-cyan'}`
                                  })}
                                </motion.div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <h4 className="text-sm font-semibold text-white truncate">
                                      {result.title}
                                    </h4>
                                    {result.badge && (
                                      <Badge className="bg-cyber-purple/20 text-cyber-purple border-cyber-purple/30 text-xs px-2">
                                        {result.badge}
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-xs text-slate-400 truncate mt-0.5">
                                    {result.description}
                                  </p>
                                </div>
                                {isSelected && (
                                  <motion.div
                                    initial={{ opacity: 0, x: -5 }}
                                    animate={{ opacity: 1, x: 0 }}
                                  >
                                    <ChevronRight className="w-5 h-5 text-cyber-cyan flex-shrink-0" />
                                  </motion.div>
                                )}
                              </motion.div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-12 text-center">
                    <Search className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">
                      {searchQuery ? 'No results found' : 'Start typing to search...'}
                    </p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-5 py-3 border-t border-card-border/30 bg-card-bg/30">
                <div className="flex items-center justify-between text-xs text-slate-500">
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1.5">
                      <kbd className="px-2 py-1 bg-card-bg rounded-md border border-slate-600 font-mono">⇧</kbd>
                      <kbd className="px-2 py-1 bg-card-bg rounded-md border border-slate-600 font-mono">↵</kbd>
                      <span className="text-slate-400">Float DM</span>
                    </span>
                    <span className="flex items-center gap-1.5">
                      <kbd className="px-2 py-1 bg-card-bg rounded-md border border-slate-600 font-mono">↑</kbd>
                      <kbd className="px-2 py-1 bg-card-bg rounded-md border border-slate-600 font-mono">↓</kbd>
                      <span className="text-slate-400">Navigate</span>
                    </span>
                    <span className="flex items-center gap-1.5">
                      <kbd className="px-2 py-1 bg-card-bg rounded-md border border-slate-600 font-mono">↵</kbd>
                      <span className="text-slate-400">Execute</span>
                    </span>
                    <span className="flex items-center gap-1.5">
                      <kbd className="px-2 py-1 bg-card-bg rounded-md border border-slate-600 font-mono">ESC</kbd>
                      <span className="text-slate-400">Close</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3 text-cyber-cyan" />
                      <span className="text-cyber-cyan font-medium">{results.filter(r => r.type === 'ai-query').length} AI</span>
                    </span>
                    <span className="text-slate-600">•</span>
                    <span className="text-slate-400">{results.length} total</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
